// @flow
import flat from 'flat';
import { difference, flow, get, isEmpty, omit, omitBy } from 'lodash';
import URI from 'urijs';
import { windowWrapper, appQueryParameters } from '@clubmed/utils';
import { getTldFromLocale, getDefaultLocaleForTld } from '@clubmed/domains-service';

import { formatTimestampDate } from 'Utils/date/dateUtils';
import { AUTHORIZED_QUERY_PARAMS, HASH_PARAMS_DELIMITER, QUERY_DATE_FORMAT } from './constants';

export const BASE_URL = 'https://www.clubmed.com';

export const getLocationSearch = (location: RouterLocation<*, *>): string =>
  new URI().search(location.queryParams).search();

export const convertLocationToURI = (location: RouterLocation<*, *>): URI =>
  new URI()
    .origin(location.origin || (typeof window !== 'undefined' ? windowWrapper.getOrigin() : ''))
    .pathname(location.pathname)
    // $FlowFixMe search? Probably to remove once Next.js is set up
    .search(location.queryParams || location.search)
    .hash(location.hash || '');

// Sorry, this was necessary to fix the Quotation Form breaking a lot of tests!
// Must be fixed with Next.js
export const convertQuotationFormLocationToHref = (location: * = {}): string =>
  new URI()
    .pathname(location.pathname)
    .search(location.queryParams || location.search)
    .toString();

export const convertLocationToHref = (location: *): string =>
  `${location.pathname}${location.search}${location.hash}`;

export const convertUrlToOrigin = (url: string): string => new URI(url).origin();

export function getHashParams(uri: * = '') {
  const hash = new URI(uri).hash();
  const hashParamsDelimiterPos = hash.indexOf(HASH_PARAMS_DELIMITER);
  const hashParamsQuery =
    hashParamsDelimiterPos !== -1
      ? hash.slice(hashParamsDelimiterPos + HASH_PARAMS_DELIMITER.length)
      : '';

  if (hashParamsQuery) {
    const hashPosInHashParamsQuery = hashParamsQuery.indexOf('#');

    return hashPosInHashParamsQuery !== -1
      ? URI.parseQuery(hashParamsQuery.slice(0, hashPosInHashParamsQuery))
      : URI.parseQuery(hashParamsQuery);
  }

  return {};
}

export function formatDateForHashParams(timestamp: *) {
  return formatTimestampDate(timestamp, QUERY_DATE_FORMAT);
}

function _removeEmptyAttributes(object) {
  return omitBy(object, isEmpty);
}

function _appendHashParamsToUrl(hashParams) {
  return url => {
    const hashParamsWithNoEmptyValues = _removeEmptyAttributes(hashParams);

    return !isEmpty(hashParamsWithNoEmptyValues)
      ? url.hash(`${HASH_PARAMS_DELIMITER}${URI.buildQuery(hashParamsWithNoEmptyValues)}`)
      : url;
  };
}

function _appendQueryParamsToUrl(queryParams) {
  return url => {
    const queryParamsWithNoEmptyValues = _removeEmptyAttributes(queryParams);

    return !isEmpty(queryParamsWithNoEmptyValues)
      ? url.setQuery(queryParamsWithNoEmptyValues)
      : url;
  };
}

export function generateUrlWithParams(url: *, params: *) {
  return flow([
    _appendQueryParamsToUrl(get(params, 'queryParams', {})),
    _appendHashParamsToUrl(get(params, 'hashParams', {})),
  ])(new URI(url)).toString();
}

export function generateUri(path: * = '', params: *) {
  return generateUrlWithParams(`${windowWrapper.getOrigin()}${path}`, params);
}

export function generateLocalizedHref(
  locale: *,
  resource: *,
  slug: *,
): {
  href: string,
  hrefLang: string,
  rel: string,
} {
  if (!locale || !resource || !slug) return {};
  const href = new URI(BASE_URL).path(`${resource}/${slug}`);
  const tld = getTldFromLocale(locale);
  if (!tld) return {};
  href.tld(tld);
  const defaultLocale = getDefaultLocaleForTld(tld);
  if (defaultLocale && defaultLocale !== locale) href.search({ locale });
  return {
    href: href.toString(),
    hrefLang: locale,
    rel: 'alternate',
  };
}

function flatObjectReducer(o, reducer) {
  return o && flat.unflatten(Object.keys(o).reduce((acc, key) => reducer(acc, key, o[key]), {}));
}

export function getQueryParameters(url: *) {
  url = url.query ? url : new URI(url);
  return url.query(true);
}

export function getSpecificQueryParam(queryParam: *) {
  if (typeof window !== 'undefined') {
    const location = windowWrapper.getLocation();
    const queryParamValue = get(getQueryParameters(location), queryParam);
    return queryParamValue;
  }
  return '';
}

function assignQueryParameters(previousUrl, nextUrl) {
  return {
    ...omit(getQueryParameters(previousUrl), Object.keys(appQueryParameters.ignore)),
    ...getQueryParameters(nextUrl),
  };
}

function filterQueryParameters(queryParameters, queryParameterNames) {
  const lowerParamNames = queryParameterNames.map(param => param && param.toLowerCase());
  return flatObjectReducer(queryParameters, (filteredObject, key, value) => {
    if (lowerParamNames.indexOf(key.toLowerCase()) > -1) {
      filteredObject[key] = value;
    }
    return filteredObject;
  });
}

function getTargetQueryParameters(previousUrl, nextUrl) {
  return filterQueryParameters(
    assignQueryParameters(previousUrl, nextUrl),
    AUTHORIZED_QUERY_PARAMS,
  );
}

export function buildTargetUrlPreservingParams(previousUrl: *, nextUrlString: *, options: *) {
  const shouldPropagateHashParams = get(options, 'propagateHashParams', false);

  const nextUrl = new URI(nextUrlString);
  const targetUrl = nextUrl.query(getTargetQueryParameters(previousUrl, nextUrl));

  const targetWithHashParams = shouldPropagateHashParams
    ? targetUrl.hash(`${previousUrl.hash()}`)
    : targetUrl;

  return targetWithHashParams;
}

export function filterUnauthorizedParams(url: *, parametersToIgnore: * = []) {
  const uriUrl = new URI(url);
  const filteredQueryParams = filterQueryParameters(
    getQueryParameters(uriUrl),
    difference(AUTHORIZED_QUERY_PARAMS, parametersToIgnore),
  );
  return uriUrl.query(filteredQueryParams).toString();
}
